import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';

/**
 * Guest-session lifecycle helpers.
 *
 * Goal
 * ----
 * Ensure guest-user tokens (`user_type = GUEST / NEW_GUEST`) are cleared
 * when the browser session truly ends (user closes all tabs, quits the
 * browser, or on mobile the OS kills the process), while preserving the
 * session across:
 *   - reloads of the same tab, and
 *   - additional tabs opened from an already-authenticated tab (e.g. the
 *     QuickAccess "open form in new tab" flow for guests).
 *
 * Why this approach
 * -----------------
 * Anything that depends on `pagehide` / `beforeunload` / `unload` is
 * unreliable on mobile browsers (Chrome / Safari on Android and iOS),
 * because the OS routinely terminates tabs without giving the page a
 * chance to run unload handlers. We therefore do NOT try to clean up on
 * tab close. Instead we treat `localStorage` as persistent and enforce
 * "session semantics" on the NEXT page load:
 *
 *   1. We keep a `sessionStorage` flag (`ACTIVE_SESSION_KEY`). Because
 *      `sessionStorage` is scoped to the tab and is wiped automatically
 *      when the tab / browser closes, the presence of that flag tells us
 *      "this tab is already a known participant in an active session".
 *   2. When a tab loads WITHOUT that flag, it is a candidate for being a
 *      brand-new browser session (in which case stale guest tokens in
 *      `localStorage` must be wiped). Before doing so, we must rule out
 *      the legitimate "new tab opened from an existing sibling" case -
 *      otherwise the QuickAccess new-tab flow would keep logging guests
 *      out mid-session.
 *   3. We rule that out with a live presence ping over `BroadcastChannel`.
 *      If any sibling tab responds inside `PRESENCE_CHECK_TIMEOUT_MS`, we
 *      join the existing session and skip cleanup. If nothing responds -
 *      which is what happens after a real browser close, a mobile process
 *      kill, or a crash - we wipe the guest tokens. This is a live check
 *      (a dead process cannot reply), so the 30s false-positive window
 *      that plagued the earlier heartbeat-based approach is gone.
 *
 * Regular (non-guest) users are never touched: the cleanup branch is
 * gated on `LOCAL_STORAGE_KEYS.IS_GUEST_SESSION === 'true'`, which is
 * only ever set by the guest auth flow (`useGuestTokenHandler.ts` /
 * `useQRFlow.ts`). The presence responder only runs inside the
 * QR-facing routes via `GuestSessionGate`.
 *
 * Storage keys
 * ------------
 * sessionStorage:
 *   ACTIVE_SESSION_KEY  → 'true' once this tab has passed the gate. Wiped
 *                         automatically on tab / browser close.
 * localStorage:
 *   IS_GUEST_SESSION    → 'true' while a guest is authenticated; cleared
 *                         by `clearGuestTokensFromLocalStorage`.
 */

const ACTIVE_SESSION_KEY = 'guest_active_session';
const BROADCAST_CHANNEL_NAME = 'guest-session-presence';
const PING_MESSAGE = 'ping';
const PONG_MESSAGE = 'pong';

/**
 * How long the fresh-load check waits for a sibling tab to reply to its
 * ping. 200ms is generous for same-origin BroadcastChannel delivery while
 * staying well under the threshold where users perceive a delay.
 */
const PRESENCE_CHECK_TIMEOUT_MS = 200;

const hasBroadcastChannel = (): boolean =>
  typeof BroadcastChannel !== 'undefined';

const isGuestSessionActive = (): boolean =>
  localStorage.getItem(LOCAL_STORAGE_KEYS.IS_GUEST_SESSION) === 'true';

const hasActiveSessionFlag = (): boolean =>
  sessionStorage.getItem(ACTIVE_SESSION_KEY) === 'true';

const setActiveSessionFlag = (): void => {
  sessionStorage.setItem(ACTIVE_SESSION_KEY, 'true');
};

const clearGuestTokensFromLocalStorage = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_ID);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.TENANT);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.IS_GUEST_SESSION);
};

/**
 * Opens a transient BroadcastChannel, broadcasts a ping, and resolves
 * `true` if any sibling tab replies with a pong within
 * `PRESENCE_CHECK_TIMEOUT_MS`. Resolves `false` otherwise (including when
 * BroadcastChannel is not supported).
 */
const pingForSiblingTab = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (!hasBroadcastChannel()) {
      resolve(false);
      return;
    }

    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    let settled = false;

    const finish = (sawSibling: boolean) => {
      if (settled) return;
      settled = true;
      channel.close();
      resolve(sawSibling);
    };

    channel.addEventListener('message', (event: MessageEvent) => {
      if (event.data === PONG_MESSAGE) finish(true);
    });

    channel.postMessage(PING_MESSAGE);

    window.setTimeout(() => finish(false), PRESENCE_CHECK_TIMEOUT_MS);
  });

/**
 * Sync check for "has this tab already gone through the gate once?".
 * Used by `GuestSessionGate` to initialise its ready state without a
 * null-render flash on reload / internal navigation.
 */
export const isGuestSessionReady = (): boolean => hasActiveSessionFlag();

/**
 * Runs on first render of `GuestSessionGate`. Implements the
 * "clear on fresh browser session, keep on sibling-present new tab" rule
 * described in the module header.
 *
 *  - Already-ready tab (reload / internal navigation): returns immediately.
 *  - No IS_GUEST_SESSION flag: nothing to clean up. Mark ready and return.
 *  - Fresh tab with stale guest tokens in localStorage: ping siblings. If
 *    any sibling tab responds, this is a "new tab from existing session"
 *    case (e.g. QuickAccess new-tab form) - keep tokens. Otherwise this
 *    is a genuine fresh browser session (including mobile process kill
 *    followed by relaunch) - wipe tokens.
 */
export const prepareGuestSession = async (): Promise<void> => {
  if (hasActiveSessionFlag()) return;

  if (!isGuestSessionActive()) {
    setActiveSessionFlag();
    return;
  }

  const hasSibling = await pingForSiblingTab();
  if (!hasSibling) {
    clearGuestTokensFromLocalStorage();
  }
  setActiveSessionFlag();
};

/**
 * Starts replying to presence pings from other tabs. Must be kept alive
 * for the lifetime of this tab (see `useGuestSessionLifecycle`). The
 * returned disposer closes the channel.
 */
export const startGuestPresenceResponder = (): (() => void) => {
  if (!hasBroadcastChannel()) return () => {};

  const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  const handleMessage = (event: MessageEvent) => {
    if (event.data === PING_MESSAGE) {
      channel.postMessage(PONG_MESSAGE);
    }
  };
  channel.addEventListener('message', handleMessage);

  return () => {
    channel.removeEventListener('message', handleMessage);
    channel.close();
  };
};

/**
 * Full, unconditional wipe of guest-session state. Used when tokens are
 * being cleared explicitly (e.g. `handleClearGuestToken` /
 * `useQRFlow#clearLocalStorage`). Does NOT touch the tokens themselves
 * (callers handle those); only removes the bookkeeping.
 */
export const purgeGuestSessionArtifacts = (): void => {
  sessionStorage.removeItem(ACTIVE_SESSION_KEY);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.IS_GUEST_SESSION);
};
